import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Download, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const RESUME = "/Rishi-Shah-Resume.pdf";

const LINES = [
  [{ t: "Rishi Shah", accent: false }, { t: " —", accent: true }],
  [{ t: "Marketing leader who", accent: false }],
  [{ t: "builds", accent: true }, { t: " the tools", accent: false }],
  [{ t: "he got tired of", accent: false }],
  [{ t: "asking for.", accent: false }],
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.085, delayChildren: 0.12 } },
};
const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] } },
};
const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-28 sm:pt-36 pb-20 sm:pb-28"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
        <div className="md:col-span-7 order-2 md:order-1">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-accent mb-6"
          >
            Head of Marketing @ Tentworks Interactive · AI Builder
          </motion.p>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-extrabold tracking-tight text-ink text-4xl sm:text-5xl lg:text-[4.25rem] leading-[1.03]"
          >
            {LINES.map((parts, i) => (
              <span key={i} className="block overflow-hidden pb-[0.06em]">
                <motion.span variants={line} className="block">
                  {parts.map((p, j) => (
                    <span key={j} className={p.accent ? "text-accent" : undefined}>
                      {p.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0.7}
            className="mt-8 max-w-xl text-base sm:text-lg text-ink-soft leading-relaxed"
          >
            I'm the Head of Marketing at Tentworks Interactive, where I've spent
            almost seven years taking games from a wishlist page to a launch day
            people actually show up for. On the side, I use AI tools like Lovable
            and Emergent to build real, working software for small businesses that
            don't have an engineering team. This site is a look at both sides of
            that.
          </motion.p>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0.85}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-work-button"
              onClick={() => scrollToId("work")}
              className="group inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-medium text-white hover:bg-accent-hover transition-colors"
            >
              View my work
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-projects-button"
              onClick={() => scrollToId("projects")}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-medium text-ink hover:border-accent hover:text-accent transition-colors"
            >
              See my projects
            </button>
            <a
              data-testid="hero-resume-button"
              href={RESUME}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-3 py-3 text-sm font-medium text-ink-soft hover:text-accent transition-colors"
            >
              <Download className="w-4 h-4" /> Download resume
            </a>
          </motion.div>
        </div>

        <div className="md:col-span-5 order-1 md:order-2 flex justify-center md:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-3 sm:-inset-4 border border-accent/30 rounded-[2rem] translate-x-3 translate-y-3" />
            <div className="relative w-56 h-72 sm:w-72 sm:h-96 md:w-80 md:h-[26rem] overflow-hidden rounded-[1.75rem] ring-1 ring-line shadow-[0_30px_60px_-30px_rgba(23,22,15,0.4)] bg-surface">
              <motion.img
                src="/headshot.jpg"
                alt="Rishi Shah, Head of Marketing and AI builder, outdoors"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 w-full h-[112%] object-cover object-center"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:-left-5 bg-surface border border-line rounded-2xl px-4 py-2.5 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                Based in
              </p>
              <p className="text-sm font-semibold text-ink">San Francisco, CA</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="hidden md:flex items-center gap-2 mt-20 text-xs font-mono uppercase tracking-[0.2em] text-muted hover:text-accent transition-colors"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll to explore
      </motion.button>
    </section>
  );
};
